<template>
  <div
    class="admin-structure relative"
    :class="{
      'structure-selected': isSelected,
      'structure-hover': isHovered && !isSelected,
      'component-outline': showOutlines
    }"
    @mouseenter="handleStructureMouseEnter"
    @mouseleave="handleStructureMouseLeave"
    @keydown="handleKeyDown"
    tabindex="0"
  >
    <!-- Clickable Structure Header Bar - Only way to select structure -->
    <div
      class="structure-header absolute -top-8 left-0 right-0 z-20 flex items-center justify-between px-3 py-1 rounded-t cursor-pointer transition-all"
      :class="isSelected ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'"
      @click.stop="handleStructureClick"
    >
      <div class="flex items-center gap-2">
        <!-- Drag handle: PageEditor arms native drag on mousedown inside .drag-handle -->
        <i
          class="pi pi-arrows-v text-sm drag-handle cursor-grab"
          :title="t('admin.dragToReorder')"
          :aria-label="t('admin.dragToReorder')"
          @click.stop
        ></i>
        <i :class="getStructureIcon(component.componentType)" class="text-sm"></i>
        <span class="font-medium text-sm">{{ getStructureName(component.componentType) }}</span>
      </div>
      <span class="text-xs opacity-75">Click to edit layout</span>
    </div>

    <!-- Structure Selection Overlay (Purple/Violet) -->
    <div
      v-if="isSelected"
      class="selection-overlay absolute inset-0 pointer-events-none z-10"
    >
      <!-- Selection Border - Purple for structure -->
      <div class="absolute inset-0 border-2 border-purple-500 bg-purple-500/5 rounded"></div>
    </div>

    <!-- Structure Settings Bar (Figma-style floating bar) - appears below header when selected -->
    <StructureSettingsBar
      v-if="isSelected"
      :structure="selectedStructureData"
      @property-change="handlePropertyChange"
      @open-full-editor="handleOpenFullEditor"
    />

    <!-- Hover Overlay - Purple tint -->
    <div
      v-else-if="isHovered"
      class="hover-overlay absolute inset-0 pointer-events-none border border-purple-400/50 bg-purple-400/5 rounded"
    ></div>

    <!-- Admin-controlled rendering of structure with grid slots -->
    <section :class="containerClasses" class="py-8 md:py-12">
      <!-- Title -->
      <h2 v-if="component.contentData.title" class="text-3xl md:text-4xl font-bold text-center text-surface-900 mb-8 md:mb-12">
        {{ component.contentData.title }}
      </h2>

      <!-- Grid with pre-rendered slots -->
      <div :class="layoutClasses">
        <!-- data-reorder-*: lets PageEditor's native drag reorder saved child instances
             within this container (inline Items have no instance id and are skipped). -->
        <div
          v-for="slotIndex in gridSlots"
          :key="slotIndex"
          class="min-h-[80px]"
          :data-reorder-id="typeof nestedItems[slotIndex]?.id === 'number' ? nestedItems[slotIndex].id : undefined"
          :data-reorder-parent="typeof nestedItems[slotIndex]?.id === 'number' ? component.id : undefined"
          :data-reorder-axis="typeof nestedItems[slotIndex]?.id === 'number' ? 'x' : undefined"
        >
          <!-- Filled slot: render child component -->
          <AdminComponentWrapper
            v-if="nestedItems[slotIndex] && nestedItems[slotIndex].content && createNestedComponentData(nestedItems[slotIndex], slotIndex)"
            :component="createNestedComponentData(nestedItems[slotIndex], slotIndex)!"
            :show-outlines="showOutlines"
            @edit="handleNestedEdit"
          />

          <!-- Empty slot: show DropZone -->
          <DropZone
            v-else
            :position="slotIndex"
            :parent-id="component.id"
            :message="'Slot ' + (slotIndex + 1)"
            @add-component="handleNestedAddComponent"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUIStore, type SelectedStructure } from '@/stores/ui'
import { useUpdateComponentMutation } from '@/composables/usePages'
import StructureSettingsBar from './StructureSettingsBar.vue'
import AdminComponentWrapper from './AdminComponentWrapper.vue'
import DropZone from './DropZone.vue'

interface Props {
  component: {
    id: number
    componentType: string
    contentData: any
    position: number
    pageId?: number
    children?: any[]
  }
  isSelected?: boolean
  isHovered?: boolean
  showOutlines?: boolean
}

const props = defineProps<Props>()

const { t } = useI18n()

const emit = defineEmits<{
  edit: [component: any]
  nestedEdit: [component: any]
  addComponent: [event: { position: number; parentId?: number }]
}>()

// UI Store
const uiStore = useUIStore()

// Mutations
const updateComponentMutation = useUpdateComponentMutation()

// Computed: Selection state
const isSelected = computed(() =>
  props.isSelected ?? (uiStore.selectedStructure?.id === props.component.id)
)

const isHovered = computed(() =>
  props.isHovered ?? (uiStore.hoveredStructure === props.component.id)
)

// Selected structure data for StructureSettingsBar
const selectedStructureData = computed<SelectedStructure | null>(() => {
  if (!isSelected.value) return null
  return {
    id: props.component.id,
    type: props.component.componentType,
    name: getStructureName(props.component.componentType),
    data: props.component.contentData
  }
})

// Get nested items from both inline data.Items and API children
const nestedItems = computed(() => {
  const data = props.component.contentData
  const inlineItems = (data && Array.isArray(data.Items)) ? data.Items : []

  // Transform API children into Items format
  const apiChildren = (props.component.children || []).map((child: any) => ({
    id: child.id,
    content: {
      __component: child.componentType,
      id: child.id,
      ...child.contentData
    }
  }))

  // API children take priority; use inline only when no API children exist
  return apiChildren.length > 0 ? apiChildren : inlineItems
})

// Array of slot indices to render (at least column count, or more if children exceed it)
const gridSlots = computed(() => {
  const data = props.component.contentData
  const columns = typeof data?.columns === 'number' ? data.columns : 2
  // Show at least as many slots as columns, but expand if more children exist
  const count = Math.max(columns, nestedItems.value.length + 1)
  return Array.from({ length: count }, (_, i) => i)
})

// Container classes based on containerWidth
const containerClasses = computed(() => {
  const width = props.component.contentData?.containerWidth || 'contained'
  const widthMap: Record<string, string> = {
    contained: 'max-w-7xl mx-auto px-4',
    wide: 'max-w-screen-2xl mx-auto px-4',
    full: 'px-4'
  }
  return widthMap[width] || widthMap.contained
})

// Layout classes based on layout type and columns
const layoutClasses = computed(() => {
  const data = props.component.contentData
  const layout = data?.layout || 'grid'
  const columns = data?.columns || 3
  const gap = data?.gap || 'medium'

  // Gap classes
  const gapMap: Record<string, string> = {
    none: 'gap-0',
    small: 'gap-4 md:gap-6',
    medium: 'gap-6 md:gap-8',
    large: 'gap-8 md:gap-12'
  }
  const gapClass = gapMap[gap] || gapMap.medium

  // Layout-specific classes
  if (layout === 'grid') {
    const colMap: Record<number | string, string> = {
      'auto': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
      5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
      6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'
    }
    return `grid ${colMap[columns] || colMap[3]} ${gapClass}`
  }

  if (layout === 'flex-row') {
    return `flex flex-wrap ${gapClass}`
  }

  if (layout === 'flex-col') {
    return `flex flex-col ${gapClass}`
  }

  if (layout === 'list') {
    return 'space-y-6'
  }

  // LayoutGrid presets
  if (layout === '2-col') return `grid grid-cols-1 md:grid-cols-2 ${gapClass}`
  if (layout === '3-col') return `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${gapClass}`
  if (layout === '4-col') return `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${gapClass}`
  if (layout === '2-col-sidebar') return `grid grid-cols-1 md:grid-cols-3 ${gapClass}`

  return `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${gapClass}`
})

// Get item classes based on item properties
const getItemClasses = (item: any): string => {
  const width = item.width
  if (!width || width === 'auto') return ''

  const widthMap: Record<string, string> = {
    'full': 'col-span-full',
    '1/2': 'md:col-span-1',
    '1/3': 'lg:col-span-1',
    '2/3': 'lg:col-span-2',
    '3/4': 'xl:col-span-3'
  }
  return widthMap[width] || ''
}

// Create component data for nested items
const createNestedComponentData = (item: any, index: number) => {
  if (!item.content) return null

  return {
    id: item.content.id || `${props.component.id}-item-${index}`,
    componentType: item.content.__component,
    contentData: item.content,
    position: index,
    pageId: props.component.pageId,
    parentInstanceId: props.component.id
  }
}

// Handle nested component edit
const handleNestedEdit = (nestedComponent: any) => {
  emit('nestedEdit', nestedComponent)
}

// Handle add component inside structure
const handleNestedAddComponent = (event: { position: number; parentId?: number }) => {
  emit('addComponent', event)
}

// Helper functions
const getStructureName = (type: string): string => {
  const nameMap: Record<string, string> = {
    'cms.flex-section': 'Flex Layout',
    'cms.layout-grid': 'Grid Layout'
  }
  return nameMap[type] || type
}

const getStructureIcon = (type: string): string => {
  const iconMap: Record<string, string> = {
    'cms.flex-section': 'pi pi-objects-column',
    'cms.layout-grid': 'pi pi-th-large'
  }
  return iconMap[type] || 'pi pi-box'
}

// Event handlers
const handleStructureClick = (event: MouseEvent) => {
  event.stopPropagation()

  // Select this structure
  uiStore.selectStructure({
    id: props.component.id,
    type: props.component.componentType,
    name: getStructureName(props.component.componentType),
    data: props.component.contentData
  })

  // Clear any content selection when clicking structure
  uiStore.clearSelection()

  console.log('Structure selected:', props.component.id, props.component.componentType)
}

const handleStructureMouseEnter = () => {
  uiStore.hoverStructure(props.component.id)
}

const handleStructureMouseLeave = () => {
  uiStore.hoverStructure(null)
}

const handleKeyDown = (event: KeyboardEvent) => {
  // Only handle if this structure is selected
  if (uiStore.selectedStructure?.id !== props.component.id) return

  // Escape - clear structure selection
  if (event.key === 'Escape') {
    uiStore.clearStructureSelection()
    event.preventDefault()
  }
}

// Handle property changes from StructureSettingsBar
const handlePropertyChange = async (property: string, value: any) => {
  if (!props.component.pageId) {
    console.error('Cannot update structure: pageId is missing')
    return
  }

  // Update local UI state immediately
  uiStore.updateComponentProperty(props.component.id, property, value)

  try {
    // Persist to backend
    await updateComponentMutation.mutateAsync({
      id: props.component.id,
      pageId: props.component.pageId,
      contentData: {
        ...props.component.contentData,
        [property]: value
      }
    })

    console.log('Structure property updated:', property, value)
  } catch (error) {
    console.error('Failed to update structure property:', error)
  }
}

// Handle opening full editor (PropertyInspector)
const handleOpenFullEditor = () => {
  emit('edit', props.component)
}
</script>

<style scoped>
.admin-structure {
  position: relative;
  margin-top: 2.5rem; /* Space for header bar */
}

.admin-structure:hover .hover-overlay {
  opacity: 1;
}

.component-outline {
  outline: 1px dashed rgba(168, 85, 247, 0.3);
  outline-offset: 2px;
}

.structure-selected {
  z-index: 10;
}

.structure-hover {
  z-index: 5;
}

.selection-overlay {
  border-radius: inherit;
}

.hover-overlay {
  border-radius: inherit;
}

/* Smooth transitions */
.admin-structure {
  transition: all 0.2s ease;
}

.selection-overlay,
.hover-overlay {
  transition: opacity 0.2s ease;
}

/* Structure header styling */
.structure-header {
  font-size: 0.875rem;
}
</style>
